import React, { useState, useEffect, useMemo } from 'react';
import { DocumentTextIcon, ArrowPathIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useXmlTerceiros } from '../hooks/useXmlTerceiros';
import type { XmlTerceirosFilters, XmlTerceiro } from '../types/xmlTerceiros';
import XmlTerceirosList from '../components/xmlTerceiros/XmlTerceirosList';
import XmlTerceirosModal from '../components/xmlTerceiros/XmlTerceirosModal';
import { money } from '../utils/format';
import { inputIris } from '../components/ui/iris';

/** Período pré-definido (dias) — chip de seleção única. */
const PERIODOS = [
  { label: 'Hoje', value: 1 },
  { label: '7 dias', value: 7 },
  { label: '15 dias', value: 15 },
  { label: '30 dias', value: 30 },
  { label: '60 dias', value: 60 },
  { label: '90 dias', value: 90 },
];

/** Filtro por status REAL do ERP (+ trânsito). Valores batem com o backend (Corresponde). */
const STATUS_ERP: { label: string; value: string }[] = [
  { label: 'Todos', value: '' },
  { label: 'Pendente', value: 'PE' },
  { label: 'Validada', value: 'VA' },
  { label: 'Faturada', value: 'FA' },
  { label: 'Em trânsito', value: 'TRANSITO' },
  { label: 'Não lançada', value: 'NL' },
];

/** KPI Íris (desktop): eyebrow maiúsculo + número grande tabular. Sem sombra, sem
 *  ícone-em-círculo — mantém o padrão das outras telas (Reposição etc). */
const Kpi: React.FC<{ label: string; value: React.ReactNode; valueClass?: string; small?: boolean }> = ({
  label, value, valueClass = 'text-tx', small,
}) => (
  <div className="flex flex-col justify-center rounded-iris border border-line bg-surface px-3.5 py-2.5">
    <span className="text-[10px] font-medium uppercase tracking-wide text-tx-3">{label}</span>
    <span className={`mt-0.5 tabular-nums ${small ? 'text-sm font-semibold' : 'text-2xl font-bold'} ${valueClass}`}>{value}</span>
  </div>
);

const XmlTerceirosPage: React.FC = () => {
  const [filters, setFilters] = useState<XmlTerceirosFilters>({ dias: 30 });
  const [selectedXml, setSelectedXml] = useState<XmlTerceiro | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Debounce da busca para não disparar query a cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const activeFilters = useMemo<XmlTerceirosFilters>(
    () => ({ ...filters, search: searchTerm || undefined }),
    [filters, searchTerm]
  );

  const { data: response, isLoading, error, refetch } = useXmlTerceiros(activeFilters, currentPage, itemsPerPage, true);
  const resumo = response?.resumo; // KPIs por status-ERP (janela inteira)

  const xmlTerceiros = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const totalItems = response?.pagination?.totalItems || 0;
  const startIndex = ((response?.pagination?.page || 1) - 1) * (response?.pagination?.pageSize || itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [filters, searchTerm, itemsPerPage]);

  const handleOpenModal = (xml: XmlTerceiro) => { setSelectedXml(xml); setIsModalOpen(true); };
  const handleCloseModal = () => { setSelectedXml(null); setIsModalOpen(false); };

  const usandoPeriodoCustom = filters.dias === null || !!filters.dataInicio || !!filters.dataFim;

  const selecionarPeriodo = (dias: number) => {
    setFilters(prev => ({ ...prev, dias, dataInicio: undefined, dataFim: undefined }));
    setCurrentPage(1);
  };

  const abrirPeriodoCustom = () => {
    const hoje = new Date();
    const dataFimDefault = hoje.toISOString().split('T')[0];
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - 30);
    setFilters(prev => ({
      ...prev,
      dias: null,
      dataInicio: prev.dataInicio || inicio.toISOString().split('T')[0],
      dataFim: prev.dataFim || dataFimDefault,
    }));
    setCurrentPage(1);
  };

  const limparTudo = () => {
    setSearchInput('');
    setSearchTerm('');
    setFilters({ dias: 30 });
    setCurrentPage(1);
  };

  // Chip de seleção — pinta com o accent quando ativo (seleção de valor, não toggle).
  const chip = (ativo: boolean) =>
    `h-9 rounded-iris px-3 text-sm font-medium transition-colors ${
      ativo ? 'bg-accent text-white' : 'bg-surface-2 text-tx-2 hover:bg-surface'
    }`;

  if (error) {
    return (
      <div className="mx-auto max-w-[1280px] space-y-4">
        <div className="rounded-iris-lg border border-danger-border bg-danger-soft p-4">
          <h3 className="text-sm font-semibold text-danger">Erro ao carregar NF-e de Terceiros</h3>
          <p className="mt-1 text-sm text-tx-2">Não foi possível carregar as notas fiscais. Tente atualizar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] space-y-4">
      {/* Header canônico Íris — refresh fica SEMPRE no topo à direita */}
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-[-0.01em] text-tx">
            <DocumentTextIcon className="h-6 w-6 flex-shrink-0 text-accent-text" />
            NF-e de Terceiros
          </h1>
          <p className="mt-0.5 text-sm text-tx-3">Notas fiscais eletrônicas recebidas de fornecedores.</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-iris px-2.5 text-sm font-medium text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-50"
          title="Atualizar lista"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Atualizar</span>
        </button>
      </header>

      {/* KPIs por status-ERP — cards no desktop; mini-tira no mobile. Sempre sobre a
          janela inteira (não muda com o filtro de status ativo). */}
      <div className="hidden gap-2.5 sm:grid sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Total de NF-e" value={resumo?.total ?? 0} />
        <Kpi label="Pendentes" value={resumo?.pendente ?? 0} valueClass="text-warning" />
        <Kpi label="Em trânsito" value={resumo?.emTransito ?? 0} valueClass="text-accent-text" />
        <Kpi label="Valor total" value={money(resumo?.valorTotal)} valueClass="text-ok" />
      </div>
      {/* Mini-KPI (mobile): total · pendentes · em trânsito */}
      <div className="flex items-baseline gap-x-4 rounded-iris border border-line bg-surface px-3.5 py-2 sm:hidden">
        <span className="text-tx">
          <span className="text-base font-bold tabular-nums">{resumo?.total ?? 0}</span>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-tx-3">NF-e</span>
        </span>
        <span>
          <span className="text-base font-bold tabular-nums text-warning">{resumo?.pendente ?? 0}</span>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-tx-3">pend.</span>
        </span>
        <span className="ml-auto">
          <span className="text-base font-bold tabular-nums text-accent-text">{resumo?.emTransito ?? 0}</span>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-tx-3">trâns.</span>
        </span>
      </div>

      {/* Deck de controle: busca + período + status */}
      <div className="rounded-iris-lg border border-line bg-surface p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tx-3" />
            <input
              type="text"
              placeholder="Buscar por emitente, CNPJ ou número da NF-e…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={`${inputIris} w-full pl-9`}
            />
          </div>

          {/* Chips de período — rolagem horizontal no mobile (não quebra), wrap no desktop */}
          <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-0.5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {PERIODOS.map((p) => (
              <button
                key={p.value}
                onClick={() => selecionarPeriodo(p.value)}
                className={`flex-shrink-0 ${chip(filters.dias === p.value && !usandoPeriodoCustom)}`}
              >
                {p.label}
              </button>
            ))}
            <button onClick={abrirPeriodoCustom} className={`flex-shrink-0 ${chip(usandoPeriodoCustom)}`}>
              Personalizado
            </button>
          </div>

          {/* Status do ERP (chips, rolagem no mobile) + Limpar */}
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-0.5 sm:flex-wrap sm:overflow-visible sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {STATUS_ERP.map((s) => (
                <button
                  key={s.label}
                  onClick={() => { setFilters(prev => ({ ...prev, erpStatus: s.value || undefined })); setCurrentPage(1); }}
                  className={`flex-shrink-0 ${chip((filters.erpStatus ?? '') === s.value)}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <button
              onClick={limparTudo}
              className="inline-flex h-9 flex-shrink-0 items-center rounded-iris px-3 text-sm font-medium text-tx-2 transition-colors hover:bg-surface-2"
            >
              Limpar
            </button>
          </div>

          {/* Intervalo personalizado */}
          {usandoPeriodoCustom && (
            <div className="flex flex-wrap items-center gap-3 rounded-iris bg-surface-2 p-3">
              <label className="flex items-center gap-2 text-sm text-tx-2">
                De:
                <input
                  type="date"
                  value={filters.dataInicio || ''}
                  onChange={(e) => { setFilters(prev => ({ ...prev, dataInicio: e.target.value || undefined, dias: null })); setCurrentPage(1); }}
                  className={inputIris}
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-tx-2">
                Até:
                <input
                  type="date"
                  value={filters.dataFim || ''}
                  onChange={(e) => { setFilters(prev => ({ ...prev, dataFim: e.target.value || undefined, dias: null })); setCurrentPage(1); }}
                  className={inputIris}
                />
              </label>
              <button
                onClick={() => { setFilters(prev => ({ ...prev, dataInicio: undefined, dataFim: undefined, dias: 30 })); setCurrentPage(1); }}
                className="text-sm text-tx-3 underline hover:text-tx-2"
              >
                Voltar pra 30 dias
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lista */}
      <XmlTerceirosList xmlTerceiros={xmlTerceiros} onSelect={handleOpenModal} isLoading={isLoading} />

      {/* Paginação */}
      {totalItems > 0 && (
        <div className="flex flex-col items-center justify-between gap-3 rounded-iris-lg border border-line bg-surface px-3 py-2.5 sm:flex-row">
          <div className="text-[13px] text-tx-2">
            <span className="font-medium text-tx">{totalItems === 0 ? 0 : startIndex + 1}</span>–
            <span className="font-medium text-tx">{Math.min(startIndex + itemsPerPage, totalItems)}</span> de{' '}
            <span className="font-medium text-tx">{totalItems}</span>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[13px] text-tx-2">
              Por página:
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className={inputIris}
              >
                {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>

            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-iris text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-40"
                  title="Página anterior"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="min-w-[64px] text-center text-[13px] font-medium tabular-nums text-tx">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-iris text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-40"
                  title="Próxima página"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {selectedXml && <XmlTerceirosModal xml={selectedXml} isOpen={isModalOpen} onClose={handleCloseModal} />}
    </div>
  );
};

export default XmlTerceirosPage;
